import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../redux/store';
import { listenToShortstayData, upsertShortstay } from '../../../../redux/shortstaysSlice';
import RoomIcon from '@mui/icons-material/Room';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TextField, Button, Box, Autocomplete } from '@mui/material';
import countries from '../../../../GLOBAL/Countries'; // 🌍 global list of countries

declare global {
  interface Window {
    google: any;
  }
}

const ShortForm3: React.FC = () => {
  const { userId, listingId } = useParams<{ userId: string; listingId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector((state: RootState) => state.shortstays.currentListing);

  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [address, setAddress] = useState({
    country: '',
    street: '',
    farmName: '',
    neighborhood: '',
    apt: '',
    zip: '',
    city: '',
    county: '',
    latitude: '',
    longitude: '',
  });

  const streetInputRef = useRef<HTMLInputElement>(null);

  // ✅ Fetch & populate existing data
  useEffect(() => {
    if (listingId) {
      const unsubscribe = dispatch(listenToShortstayData(listingId));
      // optional cleanup (if listener returns unsubscribe)
      return () => {
        if (typeof unsubscribe === 'function') unsubscribe();
      };
    }
  }, [listingId, dispatch]);

  // ✅ Populate form when listing is fetched
  useEffect(() => {
    const addr = currentListing?.address;
    if (addr) {
      setAddress((prev) => ({
        ...prev,
        ...addr,
        latitude:
          addr.latitude !== undefined && addr.latitude !== null
            ? addr.latitude.toString()
            : "",
        longitude:
          addr.longitude !== undefined && addr.longitude !== null
            ? addr.longitude.toString()
            : "",
      }));
    }
  }, [currentListing]);

  // ✅ Google Places Autocomplete
  useEffect(() => {
    if (!window.google?.maps?.places || !streetInputRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(streetInputRef.current, {
      types: ['address'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const components = place.address_components || [];
      const getComponent = (type: string) =>
        components.find((c: any) => c.types.includes(type))?.long_name || '';

      const streetNumber = getComponent('street_number');
      const route = getComponent('route');
      const city = getComponent('locality') || getComponent('administrative_area_level_2');
      const neighborhood = getComponent('sublocality') || '';
      const country = getComponent('country');
      const county = getComponent('administrative_area_level_1');
      const zip = getComponent('postal_code');
      const lat = place.geometry?.location?.lat().toString() || '';
      const lng = place.geometry?.location?.lng().toString() || '';

      const streetOnly = [streetNumber, route].filter(Boolean).join(' ');

      setAddress((prev) => ({
        ...prev,
        street: streetOnly || prev.street,
        city,
        neighborhood,
        country,
        county,
        zip,
        latitude: lat,
        longitude: lng,
      }));
    });
  }, []);

  const requiredFields = ['street', 'apt', 'city', 'county'];
  const isFieldInvalid = (field: string) => !address[field as keyof typeof address]?.trim();
  const isNextDisabled =
    requiredFields.some(isFieldInvalid) || !address.latitude || !address.longitude;

  const disableOtherFields = !address.latitude || !address.longitude;

  const handleChange = (field: string, value: string) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return alert('Geolocation not supported');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude.toString();
        const lng = pos.coords.longitude.toString();

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat: +lat, lng: +lng } }, (results: any, status: any) => {
          if (status === 'OK' && results?.[0]) {
            const components = results[0].address_components || [];
            const getComponent = (type: string) =>
              components.find((c: any) => c.types.includes(type))?.long_name || '';

            const streetNumber = getComponent('street_number');
            const route = getComponent('route');
            const city = getComponent('locality') || getComponent('administrative_area_level_2');
            const neighborhood = getComponent('sublocality') || '';
            const country = getComponent('country');
            const county = getComponent('administrative_area_level_1');
            const zip = getComponent('postal_code');
            const streetOnly = [streetNumber, route].filter(Boolean).join(' ');

            setAddress((prev) => ({
              ...prev,
              street: streetOnly || prev.street,
              city,
              neighborhood,
              country,
              county,
              zip,
              latitude: lat,
              longitude: lng,
            }));
          }
        });
      },
      (err) => {
        console.error(err);
        alert('Failed to get current location');
      }
    );
  };

  const handleNext = async () => {
    if (!listingId) return;
    setIsSaving(true);
    setSuccess(false);
    setError(null);
    try {
      await dispatch(
        upsertShortstay({
          listingId,
          data: {
            address: {
              ...address,
              latitude: Number(address.latitude),
              longitude: Number(address.longitude),
            },
          },
        })
      );
      setSuccess(true);
      setTimeout(() => {
        setIsSaving(false);
        navigate(`/shortform4/${userId}/${listingId}`);
      }, 1500);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Try again.');
      setTimeout(() => setError(null), 60000);
      setIsSaving(false);
    }
  };

  const handleBack = () => navigate(`/shortform2/${userId}/${listingId}`);

  const fields = [
    { key: 'farmName', label: 'Farm name (optional)' },
    { key: 'apt', label: 'Apt name / Door number' },
    { key: 'neighborhood', label: 'Neighborhood' },
    { key: 'zip', label: 'ZIP / Postal code' },
    { key: 'city', label: 'City' },
    { key: 'county', label: 'County / State' },
  ];

  return (
    <Box sx={{ background: '#000', color: '#fff', minHeight: '100vh', pb: 16 }}>
      {(isSaving || success || error) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(6px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          {isSaving && <CircularProgress size={64} sx={{ color: '#fff' }} />}
          {success && <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'limegreen' }} />}
          {error && (
            <Box
              sx={{
                background: '#fff',
                p: 3,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 60, color: '#f44336', mr: 2 }} />
              <h3 style={{ color: '#f44336', margin: 0 }}>{error}</h3>
            </Box>
          )}
        </Box>
      )}

      {/* Form Section */}
      <Box sx={{ px: 3, maxWidth: 600, mx: 'auto', pt: 6, pb: 10 }}>
        <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 10 }}>Confirm your address</h2>
        <p style={{ color: '#ccc', marginBottom: 20 }}>
          Your address is only shared with guests after they’ve made a reservation.
        </p>

        {/* Street field */}
        <TextField
          label="Street address *"
          value={address.street}
          onChange={(e) => handleChange('street', e.target.value)}
          inputRef={streetInputRef}
          fullWidth
          sx={{
            mb: 2,
            input: { color: '#fff' },
            label: { color: '#fff' },
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#111',
              '& fieldset': { borderColor: '#555' },
              '&:hover fieldset': { borderColor: '#bf9b30' },
              '&.Mui-focused fieldset': { borderColor: '#bf9b30' },
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        {/* Country autocomplete dropdown */}
        <Autocomplete
          freeSolo
          disableClearable
          options={countries.map((c) => c.name)}
          value={address.country || ''}
          onChange={(_, newValue) => handleChange('country', newValue || '')}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country / Region *"
              fullWidth
              sx={{
                mb: 2,
                input: { color: '#fff' },
                label: { color: '#fff' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#111',
                  '& fieldset': { borderColor: '#555' },
                  '&:hover fieldset': { borderColor: '#bf9b30' },
                  '&.Mui-focused fieldset': { borderColor: '#bf9b30' },
                },
              }}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />

        {/* Other fields */}
        {fields.map(({ key, label }) => (
          <TextField
            key={key}
            label={label + (requiredFields.includes(key) ? ' *' : '')}
            value={(address as any)[key] || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            fullWidth
            disabled={key !== 'street' && disableOtherFields}
            placeholder={label}
            sx={{
              mb: 2,
              input: { color: '#fff' },
              label: { color: '#fff' },
              '& .MuiInputBase-input::placeholder': { color: '#ccc', opacity: 1 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: disableOtherFields ? '#333' : '#111',
                '& fieldset': {
                  borderColor:
                    isFieldInvalid(key) && requiredFields.includes(key) ? 'red' : '#555',
                },
                '&:hover fieldset': { borderColor: '#bf9b30' },
                '&.Mui-focused fieldset': { borderColor: '#bf9b30' },
              },
            }}
            InputLabelProps={{ shrink: true }}
          />
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
          <Button
            onClick={handleUseCurrentLocation}
            sx={{
              background: 'linear-gradient(to right, #fff, #bf9b30)',
              color: '#000',
              borderRadius: 4,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            <RoomIcon sx={{ mr: 1 }} /> Use current location
          </Button>
        </Box>

        <p style={{ color: '#888', fontSize: 13, marginTop: 10 }}>
          Make it clear to guests where your place is located. We'll only share your address after
          reservation.
        </p>
      </Box>

      {/* Footer navigation */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#000',
          borderTop: '1px solid #333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          zIndex: 1000,
        }}
      >
        <Button
          onClick={handleBack}
          sx={{
            background: '#222',
            color: '#fff',
            borderRadius: 4,
            px: 4,
            py: 1,
            fontWeight: 600,
          }}
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={isNextDisabled}
          sx={{
            background: isNextDisabled ? '#666' : 'linear-gradient(to right, #fff, #bf9b30)',
            color: isNextDisabled ? '#ccc' : '#000',
            borderRadius: 4,
            px: 4,
            py: 1,
            fontWeight: 600,
            textTransform: 'none',
            cursor: isNextDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default ShortForm3;
